import React, { useState } from 'react';
import styled from 'styled-components';
import { Upload, CheckCircle, AlertTriangle, Building, Plus } from 'lucide-react';
import { PREDEFINED_FACILITIES } from '../../data/facilities';
import { facilityAPI } from '../../services/facilityApi';
import { CreateFacilityRequest } from '../../types/facility';

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
          &:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
`;

const FacilitiesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const FacilityCard = styled.div<{ imported: boolean }>`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  opacity: ${props => props.imported ? 0.6 : 1};
  
  ${props => props.imported && `
    background: #f0fdf4;
    border-color: #22c55e;
  `}
`;

const FacilityName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const FacilityLocation = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px 0;
`;

const FacilityType = styled.span`
  padding: 4px 8px;
  background: #f3f4f6;
  color: #374151;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const StatusIndicator = styled.div<{ imported: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.imported ? '#22c55e' : '#6b7280'};
`;

const MessageContainer = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;

  ${props => {
    switch (props.type) {
      case 'success':
        return 'background: #d1fae5; color: #065f46; border: 1px solid #22c55e;';
      case 'error':
        return 'background: #fee2e2; color: #991b1b; border: 1px solid #ef4444;';
      case 'info':
        return 'background: #dbeafe; color: #1e40af; border: 1px solid #3b82f6;';
      default:
        return 'background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;';
    }
  }}
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FacilityImport: React.FC = () => {
  const [importedFacilities, setImportedFacilities] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const handleImportAll = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const facility of PREDEFINED_FACILITIES) {
        try {
          const facilityData: CreateFacilityRequest = {
            name: facility.name,
            address: facility.address,
            contact: facility.contact,
            type: facility.type,
            status: 'Active',
            specialties: [],
            establishedDate: new Date().toISOString().split('T')[0]
          };
          
          await facilityAPI.createFacility(facilityData);
          setImportedFacilities(prev => new Set([...Array.from(prev), facility.id]));
          successCount++;
        } catch (error) {
          console.error(`Failed to import ${facility.name}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        setMessage({
          text: `Successfully imported ${successCount} facilities${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
          type: 'success'
        });
      } else {
        setMessage({
          text: 'Failed to import any facilities. They may already exist.',
          type: 'error'
        });
      }
    } catch (error) {
      setMessage({
        text: 'Failed to import facilities. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportSingle = async (facilityId: string) => {
    const facility = PREDEFINED_FACILITIES.find(f => f.id === facilityId);
    if (!facility) return;

    setLoading(true);
    setMessage(null);
    
    try {
      const facilityData: CreateFacilityRequest = {
        name: facility.name,
        address: facility.address,
        contact: facility.contact,
        type: facility.type,
        status: 'Active',
        specialties: [],
        establishedDate: new Date().toISOString().split('T')[0]
      };
      
      await facilityAPI.createFacility(facilityData);
      setImportedFacilities(prev => new Set([...Array.from(prev), facilityId]));
      setMessage({
        text: `Successfully imported ${facility.name}`,
        type: 'success'
      });
    } catch (error) {
      setMessage({
        text: `Failed to import ${facility.name}. It may already exist.`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <Building size={28} />
          Import Predefined Facilities
        </Title>
        <Button 
          variant="primary" 
          onClick={handleImportAll}
          disabled={loading}
        >
          {loading ? <LoadingSpinner /> : <Upload size={20} />}
          {loading ? 'Importing...' : 'Import All Facilities'}
        </Button>
      </Header>

      {message && (
        <MessageContainer type={message.type}>
          {message.type === 'success' ? <CheckCircle size={20} /> : 
           message.type === 'error' ? <AlertTriangle size={20} /> : 
           <Building size={20} />}
          {message.text}
        </MessageContainer>
      )}

      <FacilitiesList>
        {PREDEFINED_FACILITIES.map((facility) => {
          const isImported = importedFacilities.has(facility.id);
          
          return (
            <FacilityCard key={facility.id} imported={isImported}>
              <FacilityName>{facility.name}</FacilityName>
              <FacilityLocation>
                {facility.address.city}, {facility.address.state}
              </FacilityLocation>
              <FacilityType>{facility.type}</FacilityType>
              
              <StatusIndicator imported={isImported}>
                {isImported ? (
                  <>
                    <CheckCircle size={16} />
                    Imported
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    <Button 
                      variant="secondary" 
                      onClick={() => handleImportSingle(facility.id)}
                      disabled={loading}
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      Import
                    </Button>
                  </>
                )}
              </StatusIndicator>
            </FacilityCard>
          );
        })}
      </FacilitiesList>
    </Container>
  );
};

export default FacilityImport;
